//
// Pure helpers for the dashboard tiles — kept free of component state so
// they are unit-testable (see tests/unit/utils.dashboard.spec.js).
//

//
// Finds the momentary value of a tile in the latest-data tree that the
// 60-second monitoring poll keeps in the Vuex store
// (organizations -> facilities -> devices -> sensors -> data[0].values).
// Returns { value, recordedtime } or null when nothing matches yet.
//
export function momentaryValueForTile (organizations, tile) {
  if (!Array.isArray(organizations) || !tile || tile.sensorId == null) return null
  for (const organization of organizations) {
    for (const facility of organization.facilities || []) {
      for (const device of facility.devices || []) {
        for (const sensor of device.sensors || []) {
          if (sensor.id !== tile.sensorId) continue
          const dataPackage = sensor.data && sensor.data.length > 0 ? sensor.data[0] : null
          if (!dataPackage) return null
          for (const value of dataPackage.values || []) {
            if (value.systempart === tile.systempart && value.type === tile.valuetype) {
              return {
                value: value.value * Math.pow(10, value.factor),
                recordedtime: dataPackage.recordedtime
              }
            }
          }
          return null
        }
      }
    }
  }
  return null
}

//
// Percent change formatted for the tile: '+12,5 %' / '−3,1 %' / null.
// A consumption INCREASE is bad news -> callers color positive red.
//
export function formatChangePercent (changePercent) {
  if (changePercent == null || !isFinite(changePercent)) return null
  const rounded = Math.round(changePercent * 10) / 10
  const sign = rounded > 0 ? '+' : ''
  return sign + rounded.toLocaleString('de-DE') + ' %'
}

//
// Relative widths (0..100) for the current-vs-previous mini bars of a tile.
// The bigger of the two spans the full width; null sums yield width 0.
//
export function miniBarWidths (currentSum, previousSum) {
  const current = currentSum != null ? Math.abs(Number(currentSum)) : 0
  const previous = previousSum != null ? Math.abs(Number(previousSum)) : 0
  const max = Math.max(current, previous)
  if (max === 0) return { current: 0, previous: 0 }
  return {
    current: Math.round((current / max) * 100),
    previous: Math.round((previous / max) * 100)
  }
}

//
// Fresh tile id — crypto.randomUUID where available (all target browsers),
// with a time+random fallback for exotic embedders.
//
export function newTileId () {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'tile-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10)
}

//
// The (systempart, valuetype) pairs that exist in frmdailytotal per sensor
// type — the only meaningful choices for PERIOD tiles. Mirrors the backend's
// SensorTypeValueTypeVolumePulseMap (power/heat write Import/Export 9/10,
// gas/water write Volume 22). Everything else has no daily totals.
//
export function periodValueOptionsForType (sensorType) {
  switch (sensorType) {
    case 0: // Strom
    case 3: // Wärme
      return [
        { systempart: 4, valuetype: 9 },
        { systempart: 4, valuetype: 10 }
      ]
    case 1: // Gas
    case 2: // Wasser
      return [
        { systempart: 4, valuetype: 22 }
      ]
    default:
      return []
  }
}

//
// Value tuples of the sensor's latest package that qualify as MOMENTARY
// tile sources (vt:36 is transport metadata, not a reading).
//
export function momentaryValueOptions (sensor) {
  const dataPackage = sensor && sensor.data && sensor.data.length > 0 ? sensor.data[0] : null
  if (!dataPackage) return []
  const seen = new Set()
  const options = []
  for (const value of dataPackage.values || []) {
    if (value.type === 36) continue
    const key = value.systempart + '/' + value.type
    if (seen.has(key)) continue
    seen.add(key)
    options.push({ systempart: value.systempart, valuetype: value.type })
  }
  return options
}

const WeekdayLabels = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
const MonthLabels = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']

//
// Normalizes a server point label ('2026-08-17' or '2026-08') to the shared
// X-axis category of the overlay chart: weekday for week tiles, day-of-month
// for month tiles, month name for year tiles. Both periods map onto the SAME
// categories — that is what makes the two series overlay instead of lining
// up side by side.
//
export function overlayCategory (label, period) {
  if (period === 'year') {
    const month = parseInt(label.slice(5, 7), 10)
    return MonthLabels[month - 1] || label
  }
  if (period === 'month') {
    return parseInt(label.slice(8, 10), 10) + '.'
  }
  // week: weekday of the ISO date (UTC parse keeps it timezone-safe,
  // the label already IS the facility-local day)
  const date = new Date(label + 'T00:00:00Z')
  return isNaN(date.getTime()) ? label : WeekdayLabels[date.getUTCDay()]
}

//
// Chartkick series pair for the detail overlay chart of a period tile.
// Current period solid, previous period pale — aligned on shared categories
// (Mo..So / 1..31 / Jan..Dez) so the bars overlay by position in the period.
//
export function overlayChartSeries (tileData, period, currentName, previousName) {
  if (!tileData || !tileData.current) return []
  const categories = overlayCategories(period)
  const toData = (points) => {
    const data = {}
    for (const category of categories) {
      data[category] = null
    }
    for (const point of points || []) {
      data[overlayCategory(point.label, period)] = point.usage
    }
    return data
  }
  return [
    {
      name: currentName,
      data: toData(tileData.current.points)
    },
    {
      name: previousName,
      data: toData(tileData.previous ? tileData.previous.points : []),
      dataset: { backgroundColor: 'rgba(195, 206, 222, 0.85)', borderColor: 'rgba(195, 206, 222, 1)' }
    }
  ]
}

//
// The full fixed category list of a period — pre-seeding the series with it
// keeps the X axis complete and both series aligned even with data gaps.
//
export function overlayCategories (period) {
  if (period === 'year') return MonthLabels.slice()
  if (period === 'month') {
    const categories = []
    for (let day = 1; day <= 31; day++) categories.push(day + '.')
    return categories
  }
  return ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']
}

//
// Resolves the sensor a tile references from the latest-data tree —
// used for default titles and for showing where a tile points to.
//
export function sensorForTile (organizations, tile) {
  if (!Array.isArray(organizations) || !tile) return null
  for (const organization of organizations) {
    for (const facility of organization.facilities || []) {
      for (const device of facility.devices || []) {
        if (tile.sensorId != null) {
          for (const sensor of device.sensors || []) {
            if (sensor.id === tile.sensorId) {
              return { sensor, facility }
            }
          }
        }
        if (tile.collectionAddress) {
          for (const collection of device.collections || []) {
            if (collection.hexaddress === tile.collectionAddress) {
              return { sensor: collection, facility }
            }
          }
        }
      }
    }
  }
  return null
}
